import { useState, useEffect } from 'react';
import apiClient from '../../utils/api';
import { LEGAL_PAGES } from '../../config/legalPages';
import { fetchStaticTermsMarkdown } from '../../utils/legalPageContent';

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const AdminLegalPagesPanel = ({ addToast }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSlug, setSelectedSlug] = useState(LEGAL_PAGES[0].slug);
  const [form, setForm] = useState({ title: '', body: '' });
  const [published, setPublished] = useState(false);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const loadList = async () => {
    setLoading(true);
    try {
      const res = await apiClient.getAdminLegalPages();
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      addToast(err.message || 'Could not load legal pages', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadPage = async (slug) => {
    const meta = LEGAL_PAGES.find((p) => p.slug === slug);
    let row = { title: meta?.title || '', body: '', published: false, exists: false };

    try {
      const res = await apiClient.getAdminLegalPage(slug);
      row = { ...row, ...res.data, exists: true };
    } catch (err) {
      if (slug !== 'terms-of-service') {
        addToast(err.message || 'Could not load page', 'error');
        return;
      }
    }

    const title = row.title || meta?.title || '';
    const body = row.body || '';

    setForm({ title, body });
    setPublished(!!row.published);
  };

  const importWrittenTerms = async () => {
    if (!window.confirm('Replace the editor with the original site file (terms-of-service.md)? Unsaved changes will be lost.')) return;
    try {
      const staticBody = await fetchStaticTermsMarkdown();
      setForm((prev) => ({ ...prev, title: prev.title || 'Terms of Service', body: staticBody }));
      addToast('Loaded the original site file into the editor — Save & publish to apply', 'info');
    } catch (err) {
      addToast(err.message || 'Could not load Terms file', 'error');
    }
  };

  useEffect(() => {
    loadList();
  }, []);

  useEffect(() => {
    if (selectedSlug) loadPage(selectedSlug);
  }, [selectedSlug]);

  const save = async (publishNow = false) => {
    setSaving(true);
    try {
      const updateRes = await apiClient.updateLegalPage(selectedSlug, form);
      let saved = updateRes.data || form;
      if (publishNow) {
        const publishRes = await apiClient.publishLegalPage(selectedSlug);
        saved = publishRes.data || saved;
      }
      setForm({ title: saved.title || form.title, body: saved.body ?? form.body });
      setPublished(!!saved.published);
      addToast(publishNow ? 'Page published' : 'Draft saved', 'success');
      await loadList();
    } catch (err) {
      addToast(err.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async () => {
    try {
      if (published) await apiClient.unpublishLegalPage(selectedSlug);
      else await apiClient.publishLegalPage(selectedSlug);
      addToast(published ? 'Unpublished' : 'Published', 'success');
      await loadList();
      await loadPage(selectedSlug);
    } catch (err) {
      addToast(err.message || 'Update failed', 'error');
    }
  };

  const seedDefaults = async () => {
    if (!window.confirm('Create default drafts for any missing pages? Existing pages are kept unless you choose overwrite in a future option.')) return;
    setSeeding(true);
    try {
      const res = await apiClient.seedDefaultLegalPages();
      const { created = 0, skipped = 0 } = res.data || {};
      addToast(`Seeded ${created} page(s)${skipped ? `, ${skipped} already existed` : ''}`, 'info');
      await loadList();
      await loadPage(selectedSlug);
    } catch (err) {
      addToast(err.message || 'Seed failed', 'error');
    } finally {
      setSeeding(false);
    }
  };

  const currentMeta = LEGAL_PAGES.find((p) => p.slug === selectedSlug);
  const currentRow = rows.find((r) => r.slug === selectedSlug);

  return (
    <div>
      <div className="admin-panel-head">
        <div>
          <h3><i className="fas fa-gavel"></i> Legal &amp; policy pages</h3>
          <p className="admin-panel-sub">
            Edit footer legal pages (Privacy, Terms, SEC disclosures, etc.). Markdown supported. The homepage &quot;How CoOwn Works&quot; section is not edited here.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-outline btn-sm" onClick={loadList} disabled={loading}>
            <i className={`fas fa-sync${loading ? ' fa-spin' : ''}`}></i> Refresh
          </button>
          <button type="button" className="btn btn-outline btn-sm" onClick={seedDefaults} disabled={seeding}>
            {seeding ? 'Seeding…' : 'Load default drafts'}
          </button>
        </div>
      </div>

      <div className="form-row" style={{ marginBottom: '1rem', alignItems: 'flex-end' }}>
        <div className="form-group" style={{ flex: 1, minWidth: 220 }}>
          <label>Page</label>
          <select value={selectedSlug} onChange={(e) => setSelectedSlug(e.target.value)}>
            {LEGAL_PAGES.map((p) => (
              <option key={p.slug} value={p.slug}>{p.title}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Status</label>
          <div style={{ padding: '0.55rem 0' }}>
            {published ? <span className="badge badge-success">Live on site</span> : <span className="badge">Draft</span>}
            {currentRow?.updatedAt && (
              <span style={{ marginLeft: '0.5rem', fontSize: '0.8rem', color: 'var(--text-light)' }}>
                Updated {formatDate(currentRow.updatedAt)}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="insights-admin-form">
        <div className="form-group">
          <label>Title</label>
          <input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder={currentMeta?.title}
          />
        </div>
        <div className="form-group">
          <label>Body (markdown)</label>
          <textarea
            rows={18}
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            placeholder={`# ${currentMeta?.title || 'Page title'}\n\nWrite content here…`}
            style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.88rem' }}
          />
        </div>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-light)', marginTop: '-0.5rem', marginBottom: '1rem' }}>
          Public URL: <strong>{currentMeta?.path}</strong> · Use headings (<code>##</code>), lists (<code>- item</code>), and <code>**bold**</code>.
        </p>
        {selectedSlug === 'terms-of-service' && (
          <p style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginBottom: '1rem', padding: '0.75rem 1rem', background: 'var(--primary-tint)', borderRadius: '10px' }}>
            The editor always shows your <strong>saved</strong> Terms (what visitors see after publish). Use <strong>Load written Terms</strong> only if you want to reset from the original site file.
          </p>
        )}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {selectedSlug === 'terms-of-service' && (
            <button type="button" className="btn btn-outline btn-sm" onClick={importWrittenTerms}>
              Load written Terms
            </button>
          )}
          <button type="button" className="btn btn-outline btn-sm" disabled={saving} onClick={() => save(false)}>
            {saving ? 'Saving…' : 'Save draft'}
          </button>
          <button type="button" className="btn btn-primary btn-sm" disabled={saving} onClick={() => save(true)}>
            {saving ? 'Saving…' : 'Save & publish'}
          </button>
          {currentRow?.exists !== false && (
            <button type="button" className="btn btn-outline btn-sm" onClick={togglePublish}>
              {published ? 'Unpublish' : 'Publish'}
            </button>
          )}
        </div>
      </div>

      {!loading && rows.length > 0 && (
        <div className="admin-table-wrap" style={{ marginTop: '1.5rem' }}>
          <table className="investment-table insights-admin-table">
            <thead>
              <tr>
                <th>Page</th>
                <th>Status</th>
                <th>Last updated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.slug}>
                  <td><strong>{row.title}</strong><br /><span style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>{LEGAL_PAGES.find((p) => p.slug === row.slug)?.path}</span></td>
                  <td>{row.published ? <span className="badge badge-success">Live</span> : <span className="badge">Draft</span>}</td>
                  <td>{formatDate(row.updatedAt)}</td>
                  <td>
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => setSelectedSlug(row.slug)}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminLegalPagesPanel;
